import { decodeAgentIdentity, type AgentId, type CapabilityState, type CliVariantId } from "@orni/sdk-typescript";
import type { ProviderInventory, ProviderInventoryItem } from "./types";

/**
 * Desktop's compatibility facade for the existing Provider inventory.
 *
 * This is deliberately a projection only: the Daemon remains authoritative
 * and existing Provider APIs/fields stay intact until the Agent endpoint is
 * available. Keeping the mapping here prevents future pages from inventing
 * their own `claude`/`claude-code` interpretation.
 */
export interface DesktopAgentInventoryItem {
  agentId: AgentId;
  cliVariantId: CliVariantId;
  /** Legacy value retained for old commands and deep links. */
  providerId: ProviderInventoryItem["providerId"];
  displayName: string;
  installed: boolean;
  enabled: boolean;
  installationState: "not_installed" | "installed" | "disabled";
  capabilityState: CapabilityState;
  adapterId: string | null;
  adapterVersion: string | null;
  executablePath: string | null;
  version: string | null;
  homeDirectory: string;
  revision: number;
  lastCheckedAt: string;
}

export interface DesktopAgentInventory {
  agents: DesktopAgentInventoryItem[];
  checkedAt: string;
}

export function projectProviderInventoryToAgents(
  inventory: ProviderInventory,
): DesktopAgentInventory {
  return {
    agents: inventory.providers.map(projectProviderInventoryItemToAgent),
    checkedAt: inventory.checkedAt,
  };
}

export function projectProviderInventoryItemToAgent(
  provider: ProviderInventoryItem,
): DesktopAgentInventoryItem {
  // Prefer the canonical identity projected by the Daemon. Legacy providerId
  // is retained for existing command/settings actions and may intentionally
  // be the conservative `codex` fallback for an unknown Agent.
  const identity = decodeAgentIdentity({
    provider_id: provider.projectedProviderId ?? provider.providerId,
    ...(provider.agentId ? { agent_id: provider.agentId } : {}),
    ...(provider.cliVariantId ? { cli_variant_id: provider.cliVariantId } : {}),
  });
  const installationState = !provider.installed
    ? "not_installed"
    : provider.enabled ? "installed" : "disabled";
  return {
    agentId: identity.agentId,
    cliVariantId: identity.cliVariantId,
    providerId: provider.providerId,
    displayName: provider.displayName,
    installed: provider.installed,
    enabled: provider.enabled,
    installationState,
    // supportsNative is an observed capability, not an installation claim.
    capabilityState: provider.candidate
      ? "unknown"
      : !provider.supportsNative
      ? "unsupported"
      : provider.installed ? "verified" : "unknown",
    adapterId: provider.adapterId ?? null,
    adapterVersion: provider.adapterVersion ?? null,
    executablePath: provider.executablePath,
    version: provider.version,
    homeDirectory: provider.homeDirectory,
    revision: provider.revision,
    lastCheckedAt: provider.lastCheckedAt,
  };
}
